import AsyncStorage from "@react-native-async-storage/async-storage";
import { normalizeHost } from "../utils/websiteBlockerCore";

const STORAGE_KEY = "pc_blocklist_domains_v1";
const FILTER_ENABLED_KEY = "pc_filter_enabled_v1";
const NEXTDNS_TOKEN_KEY = "pc_nextdns_token_v1";
/** Advanced parental option: substring block on anime / manga / cartoon / movie-ish hostnames (DNS layer). */
const ADVANCED_MEDIA_KEYWORDS_ENABLED_KEY = "pc_advanced_media_keywords_v1";
/** Force Google SafeSearch + YouTube Restricted Mode via DNS VIP remapping. Default ON. */
const GOOGLE_YOUTUBE_SAFE_MODE_KEY = "pc_google_youtube_safe_mode_v1";

let cached = null;

function uniqueSorted(input) {
  return Array.from(new Set(input)).sort((a, b) => a.localeCompare(b));
}

function cleanDomain(value) {
  const host = normalizeHost(value);
  if (!host) return "";
  if (host.includes(" ")) return "";
  return host;
}

async function persist(list) {
  cached = uniqueSorted(list.map(cleanDomain).filter(Boolean));
  await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(cached));
  return cached;
}

export async function getBlocklist() {
  if (cached) return cached;
  const raw = await AsyncStorage.getItem(STORAGE_KEY);
  if (!raw) {
    cached = [];
    return cached;
  }
  try {
    const parsed = JSON.parse(raw);
    cached = uniqueSorted((Array.isArray(parsed) ? parsed : []).map(cleanDomain).filter(Boolean));
  } catch {
    cached = [];
  }
  return cached;
}

export async function addDomain(domainInput) {
  const domain = cleanDomain(domainInput);
  if (!domain) return getBlocklist();
  const current = await getBlocklist();
  return persist([...current, domain]);
}

export async function removeDomain(domainInput) {
  const domain = cleanDomain(domainInput);
  if (!domain) return getBlocklist();
  const current = await getBlocklist();
  return persist(current.filter((d) => d !== domain));
}

export async function isBlocked(domainInput) {
  const host = cleanDomain(domainInput);
  if (!host) return false;
  const list = await getBlocklist();
  return list.some((d) => host === d || host.endsWith(`.${d}`));
}

export async function setFilterEnabled(enabled) {
  await AsyncStorage.setItem(FILTER_ENABLED_KEY, enabled ? "1" : "0");
}

export async function getFilterEnabled() {
  const raw = await AsyncStorage.getItem(FILTER_ENABLED_KEY);
  return raw === "1";
}

export async function setNextDnsToken(token) {
  await AsyncStorage.setItem(NEXTDNS_TOKEN_KEY, String(token || "").trim());
}

export async function getNextDnsToken() {
  return (await AsyncStorage.getItem(NEXTDNS_TOKEN_KEY)) || "";
}

export async function setAdvancedMediaKeywordsEnabled(enabled) {
  await AsyncStorage.setItem(ADVANCED_MEDIA_KEYWORDS_ENABLED_KEY, enabled ? "1" : "0");
}

export async function getAdvancedMediaKeywordsEnabled() {
  const raw = await AsyncStorage.getItem(ADVANCED_MEDIA_KEYWORDS_ENABLED_KEY);
  return raw === "1";
}

export async function setGoogleYoutubeSafeModeEnabled(enabled) {
  await AsyncStorage.setItem(GOOGLE_YOUTUBE_SAFE_MODE_KEY, enabled ? "1" : "0");
}

/** Default ON when never set (Adult-friendly browsing without blocking Google/YouTube entirely). */
export async function getGoogleYoutubeSafeModeEnabled() {
  const raw = await AsyncStorage.getItem(GOOGLE_YOUTUBE_SAFE_MODE_KEY);
  if (raw == null) return true;
  return raw === "1";
}
